<template>
  <div class="workoutNumberStepper">
    <button
      type="button"
      class="workoutNumberStepper__btn workoutNumberStepper__btn--dec"
      :disabled="disabled || atMin"
      :aria-label="`${ariaLabel} 减少`"
      @mousedown.prevent="armBlurGuard"
      @click="onStepClick(-1)"
    >
      −
    </button>
    <!-- text + inputmode：避免 type=number 吃掉尾随小数点，打断 MET 如 3.5 / 15 -->
    <el-input
      :value="inputText"
      type="text"
      inputmode="decimal"
      size="small"
      class="workoutNumberStepper__input"
      :aria-label="ariaLabel"
      :disabled="disabled"
      @input="onInput"
      @blur="onBlur"
    />
    <button
      type="button"
      class="workoutNumberStepper__btn workoutNumberStepper__btn--inc"
      :disabled="disabled || atMax"
      :aria-label="`${ariaLabel} 增加`"
      @mousedown.prevent="armBlurGuard"
      @click="onStepClick(1)"
    >
      +
    </button>
  </div>
</template>

<script>
import {
  STEP_BLUR_GUARD_MS,
  clampStepperValue,
  coerceStepperValue,
  computeSteppedValue,
  formatStepperInputText,
  inputWithinBounds,
  isAtMax,
  isAtMin,
  isIncompleteNumericDraft,
} from './workoutNumberStepperLogic'

/**
 * 数字步进器：props 可传 number/string；编辑中允许空串与尾随「.」，失焦再 clamp。
 * 不用 el-input @change——点 +/- 触发的 blur/change 会用旧值覆盖步进结果。
 * 编辑中保留 draftText，勿在 value 回写时清空（否则 toFixed 会打断 3.→3.5）。
 */
export default {
  name: 'WorkoutNumberStepper',
  props: {
    value: { type: [Number, String], default: 0 },
    min: { type: Number, default: 0 },
    max: { type: Number, default: Infinity },
    step: { type: Number, default: 1 },
    precision: { type: Number, default: 0 },
    ariaLabel: { type: String, default: '数值' },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      draftText: null,
      ignoreBlurUntil: 0,
    }
  },
  computed: {
    stepperBounds() {
      return { min: this.min, max: this.max, precision: this.precision }
    },
    coercedValue() {
      return coerceStepperValue(this.value, this.precision)
    },
    inputText() {
      return formatStepperInputText(this.value, this.precision, this.draftText)
    },
    atMin() {
      return isAtMin(this.value, this.min, this.precision)
    },
    atMax() {
      return isAtMax(this.value, this.min, this.max, this.precision)
    },
  },
  methods: {
    armBlurGuard() {
      this.ignoreBlurUntil = Date.now() + STEP_BLUR_GUARD_MS
    },
    emitClamped(next) {
      const clamped = clampStepperValue(next, this.stepperBounds)
      // 输入过程中保留 draftText（如 "3."）；仅 blur / 步进按钮时清空
      this.$emit('input', clamped)
      return clamped
    },
    onStepClick(delta) {
      if (this.disabled) {
        return
      }
      const stepped = computeSteppedValue(this.value, {
        min: this.min,
        max: this.max,
        step: this.step,
        precision: this.precision,
        delta,
      })
      this.draftText = null
      this.armBlurGuard()
      this.$emit('input', stepped)
    },
    onInput(raw) {
      const text = raw == null ? '' : String(raw)
      this.draftText = text
      if (isIncompleteNumericDraft(text)) {
        return
      }
      const parsed = coerceStepperValue(text, this.precision)
      if (parsed == null) {
        return
      }
      if (inputWithinBounds(parsed, this.min, this.max)) {
        this.emitClamped(parsed)
      }
    },
    onBlur() {
      if (Date.now() < this.ignoreBlurUntil) {
        return
      }
      if (this.draftText === '') {
        if (this.coercedValue != null) {
          this.draftText = null
          return
        }
        this.emitClamped(this.min)
      } else if (this.draftText != null) {
        this.emitClamped(this.draftText)
      }
      this.draftText = null
    },
  },
}
</script>

<style scoped>
.workoutNumberStepper {
  box-sizing: border-box;
  display: inline-flex;
  align-items: stretch;
  width: 132px;
  max-width: 100%;
  border: 1px solid var(--link-line-strong, #d6d3d1);
  border-radius: 8px;
  overflow: hidden;
  background: var(--link-surface, #fff);
}

.workoutNumberStepper__btn {
  flex: 0 0 32px;
  margin: 0;
  padding: 0;
  border: none;
  background: var(--link-field-bg, #f3f0ea);
  color: var(--link-ink-secondary, #4a4541);
  font-size: 16px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.workoutNumberStepper__btn:hover:not(:disabled) {
  background: var(--link-accent-bg, #f5efe3);
  color: var(--link-accent, #a67c52);
}

.workoutNumberStepper__btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.workoutNumberStepper__btn--dec {
  border-right: 1px solid var(--link-line-strong, #e5dfd6);
}

.workoutNumberStepper__btn--inc {
  border-left: 1px solid var(--link-line-strong, #e5dfd6);
}

.workoutNumberStepper__input {
  flex: 1 1 auto;
  min-width: 0;
}

.workoutNumberStepper__input >>> .el-input__inner {
  height: 32px;
  line-height: 32px;
  padding: 0 4px;
  border: none;
  border-radius: 0;
  text-align: center;
  background: var(--link-surface, #fff);
  color: var(--link-ink);
  -moz-appearance: textfield;
}

.workoutNumberStepper__input >>> .el-input__inner::-webkit-outer-spin-button,
.workoutNumberStepper__input >>> .el-input__inner::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.workoutNumberStepper__input >>> .el-input__inner:focus {
  box-shadow: inset 0 0 0 1px var(--link-accent-border, #cce5ff);
}
</style>
